import React, { useState } from "react";
import { ongoingJobsApi } from "../api/routes";
import { Button, Card, Input, Select, Toast } from "../components/ui/Controls";
import { usePanelCache } from "../state/PanelCacheContext";

const statusOptions = [
  { label: "Running", value: "RUNNING" },
  { label: "Succeeded", value: "SUCCEEDED" },
  { label: "Failed", value: "FAILED" },
  { label: "Timed out", value: "TIMED_OUT" },
  { label: "Complete", value: "COMPLETE" },
  { label: "Complete with errors", value: "COMPLETE_WITH_ERRORS" },
];

const formatEpoch = (value) => {
  if (!value && value !== 0) return "—";
  const raw = Number(value);
  if (Number.isNaN(raw)) return String(value);
  const ms = raw < 1000000000000 ? raw * 1000 : raw;
  return new Date(ms).toLocaleString();
};

const statusColor = (status) => {
  if (status === "SUCCEEDED" || status === "COMPLETE") return "#0c9a4b";
  if (status === "FAILED" || status === "TIMED_OUT" || status === "COMPLETE_WITH_ERRORS") return "#c0392b";
  return "#0f172a";
};

export default function OngoingJobsPanel() {
  const [status, setStatus] = useState("RUNNING");
  const [limit, setLimit] = useState("100");
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const [panelState, setPanelState] = usePanelCache("ongoingJobs");
  const results = panelState.results || [];
  const showSearch = panelState.showSearch ?? true;

  const fetchByStatus = async () => {
    if (!status) return;
    setLoading(true);
    try {
      const res = await ongoingJobsApi.byStatus(status, limit || undefined);
      const list = Array.isArray(res.data) ? res.data : [];
      setPanelState({ results: list, showSearch: false });
    } catch (e) {
      setPanelState({ results: [] });
      setToast({ message: e.message || "Error fetching jobs", tone: "error" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Card
        title="Ongoing Jobs"
        action={
          <div style={{ display: "flex", gap: 8 }}>
            <Button tone="ghost" onClick={() => setPanelState({ results: [] })}>Clear</Button>
            <Button tone="ghost" onClick={() => setPanelState({ showSearch: !showSearch })}>
              {showSearch ? "Hide search" : "Show search"}
            </Button>
          </div>
        }
      >
        {showSearch && (
          <Card title="Search by status">
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px,1fr))", gap: 10 }}>
              <Select label="Status" value={status} onChange={setStatus} options={statusOptions} />
              <Input label="Limit" value={limit} onChange={setLimit} placeholder="100" />
            </div>
            <Button onClick={fetchByStatus} disabled={!status} loading={loading}>Search</Button>
          </Card>
        )}

        {results.length === 0 ? (
          <div style={{ padding: 12, color: "var(--stone-700)" }}>No jobs found. Search by status.</div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 980 }}>
              <thead>
                <tr style={{ background: "rgba(25,164,99,0.08)" }}>
                  <th style={{ padding: 8, textAlign: "left" }}>Group ID</th>
                  <th style={{ padding: 8, textAlign: "left" }}>Job ID</th>
                  <th style={{ padding: 8, textAlign: "left" }}>Status</th>
                  <th style={{ padding: 8, textAlign: "left" }}>Type</th>
                  <th style={{ padding: 8, textAlign: "left" }}>User ID</th>
                  <th style={{ padding: 8, textAlign: "left" }}>Conversation ID</th>
                  <th style={{ padding: 8, textAlign: "left" }}>Deadline</th>
                  <th style={{ padding: 8, textAlign: "left" }}>Expires</th>
                  <th style={{ padding: 8, textAlign: "left" }}>Inbound Message</th>
                  <th style={{ padding: 8, textAlign: "left" }}>Result Summary</th>
                  <th style={{ padding: 8, textAlign: "left" }}>Error Code</th>
                  <th style={{ padding: 8, textAlign: "left" }}>Error Message</th>
                </tr>
              </thead>
              <tbody>
                {results.map((job) => (
                  <tr key={`${job.jobGroupId}-${job.jobId}`} style={{ borderBottom: "1px solid rgba(15,23,42,0.06)" }}>
                    <td style={{ padding: 8, fontFamily: "monospace", fontSize: 12 }}>{job.jobGroupId}</td>
                    <td style={{ padding: 8, fontFamily: "monospace", fontSize: 12 }}>{job.jobId}</td>
                    <td style={{ padding: 8, fontWeight: 700, color: statusColor(job.status) }}>{job.status || "—"}</td>
                    <td style={{ padding: 8 }}>{job.type || "—"}</td>
                    <td style={{ padding: 8, fontFamily: "monospace", fontSize: 12 }}>{job.userId || "—"}</td>
                    <td style={{ padding: 8, fontFamily: "monospace", fontSize: 12 }}>{job.conversationId || "—"}</td>
                    <td style={{ padding: 8, fontSize: 12 }}>{formatEpoch(job.deadlineAt)}</td>
                    <td style={{ padding: 8, fontSize: 12 }}>{formatEpoch(job.expiresAt)}</td>
                    <td style={{ padding: 8, fontFamily: "monospace", fontSize: 12 }}>{job.inboundMessageSk || "—"}</td>
                    <td style={{ padding: 8, maxWidth: 220, whiteSpace: "normal" }}>{job.resultSummary || "—"}</td>
                    <td style={{ padding: 8 }}>{job.errorCode || "—"}</td>
                    <td style={{ padding: 8, maxWidth: 240, whiteSpace: "normal" }}>{job.errorMessage || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
      <Toast toast={toast} onClose={() => setToast(null)} />
    </>
  );
}
